import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { enquiryService } from "@/services";
import { rememberThread } from "@/lib/support-thread-store";

import { useToast } from "@/hooks/use-toast";
import { Menu, X } from "lucide-react";
import { useSiteContent } from "@/hooks/useSiteContent";
import { LANDING_SECTION, LANDING_DEFAULTS } from "@/content/landing";
import { useSiteAsset } from "@/hooks/useSiteAsset";
import { HeroVideo } from "@/components/landing/HeroVideo";
import ContextSwitcher from "@/components/ContextSwitcher";
import { useAuth } from "@/lib/auth-context";
import { useReferralTerms, resolveRewardCopy } from "@/lib/referral-terms";
import { useProgrammeFigures, resolveFigures } from "@/lib/programme-figures";




const Star = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 20 20" className={className} aria-hidden>
    <path
      d="M10 2.2l2.4 4.9 5.4.8-3.9 3.8.9 5.4-4.8-2.5-4.8 2.5.9-5.4L2.2 7.9l5.4-.8z"
      fill="currentColor"
    />
  </svg>
);

const eyebrow = "text-[12px] font-semibold uppercase tracking-[0.14em] text-slate-muted";
const h2Class =
  "font-brand font-semibold text-[clamp(2rem,4.4vw,46px)] leading-[1.05] tracking-[-0.03em]";
const shell = "mx-auto w-full max-w-[1180px] px-6 sm:px-8";

const Index = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  // All user-facing copy resolves through the content store; defaults render
  // immediately and are used whenever a key has no stored override.
  const { t } = useSiteContent(LANDING_SECTION, LANDING_DEFAULTS);
  const referralTerms = useReferralTerms();
  /* The fee and load-limit figures resolve from programme config, never typed.
     A sentence whose figure cannot be resolved is DROPPED, not shown with a default. */
  const figures = useProgrammeFigures();

  // Media assets are editable too — operator uploads resolve to signed URLs.
  const heroVideoUrl = useSiteAsset(t("landing.media.hero.video"));
  const heroPosterUrl = useSiteAsset(t("landing.media.hero.poster"));
  const styleCatUrl = useSiteAsset(t("landing.media.style.cat"));
  const styleDogUrl = useSiteAsset(t("landing.media.style.dog"));

  // UTMs and any other query params ride through to the funnel.
  const funnelHref = `/create${location.search}`;

  const { user } = useAuth();

  const [menuOpen, setMenuOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [ticketId, setTicketId] = useState<string | null>(null);
  const [bank, setBank] = useState({ name: "", org: "", email: "", note: "" });




  const ready =
    !!bank.name.trim() &&
    !!bank.org.trim() &&
    /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(bank.email.trim());

  const sendBank = async () => {
    if (!ready || busy) return;
    setBusy(true);
    // §2c: this writes a support ticket (source='website_enquiry'), not a row in
    // a separate enquiries store. Anonymous enquirer, so we keep the returned
    // access_token locally to let them follow the thread.
    const { data, error } = await enquiryService.createEnquiry({
      company_name: bank.org,
      contact_name: bank.name,
      email: bank.email,
      message: bank.note || null,
    });
    if (error || !data) {
      toast({
        title: "Something went wrong",
        description: error?.message ?? "Your enquiry could not be sent.",
        variant: "destructive",
      });
    } else {
      rememberThread({
        ticketId: data.ticketId,
        token: data.accessToken,
        subject: `Partnership enquiry — ${bank.org.trim()}`,
        email: bank.email.trim(),
      });
      setTicketId(data.ticketId);
      setSent(true);
    }
    setBusy(false);
  };


  const navLinks = [
    { href: "#how", label: t("landing.nav.how") },
    { href: "#creators", label: t("landing.nav.creators") },
    { href: "#banks", label: t("landing.nav.banks") },
    { href: "#about", label: t("landing.nav.about") },
  ];

  /* L15 · the referral figures are resolved from program_referral_settings, not
     typed into copy. A stat whose money token cannot be resolved (nothing
     configured) is dropped rather than shown with a default. */
  const creatorStats = [
    {
      n: resolveRewardCopy(t("landing.creators.stat1.value"), referralTerms),
      strong: t("landing.creators.stat1.strong"),
      rest: t("landing.creators.stat1.rest"),
    },
    {
      n: resolveRewardCopy(t("landing.creators.stat2.value"), referralTerms),
      strong: t("landing.creators.stat2.strong"),
      rest: t("landing.creators.stat2.rest"),
    },
    {
      n: resolveRewardCopy(t("landing.creators.stat3.value"), referralTerms),
      strong: t("landing.creators.stat3.strong"),
      rest: t("landing.creators.stat3.rest"),
    },
  ].filter((s): s is { n: string; strong: string; rest: string } => Boolean(s.n));

  const bankCards = [
    { t: t("landing.banks.card1.title"), d: t("landing.banks.card1.body") },
    { t: t("landing.banks.card2.title"), d: t("landing.banks.card2.body") },
    { t: t("landing.banks.card3.title"), d: t("landing.banks.card3.body") },
    { t: t("landing.banks.card4.title"), d: resolveFigures(t("landing.banks.card4.body"), figures) },
  ];

  const fieldClass =
    "w-full min-h-[52px] rounded-xl border-0 bg-white px-4 py-3.5 font-brand-body text-base text-ink outline-none focus:ring-[3px] focus:ring-volt";

  return (
    <div className="min-h-screen bg-paper font-brand-body text-ink [scroll-behavior:smooth]">
      {/* Nav */}
      <header className="sticky top-0 z-20 border-b border-ink/10 bg-paper/95 backdrop-blur-md">
        {/* Tighter gaps below md: at 390px the 28px desktop gaps plus the round
            identity control pushed the menu button off the edge. */}
        <div className={`${shell} flex items-center gap-3 py-3.5 md:gap-7`}>
          <Link to="/" className="font-brand text-[23px] font-semibold tracking-[-0.02em]">
            {t("landing.nav.brand")}
          </Link>
          <nav aria-label="Primary" className="hidden items-center gap-7 lg:flex">
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="text-[15px] font-medium text-ink transition-colors hover:text-grape"
              >
                {l.label}
              </a>
            ))}
          </nav>
          <span className="flex-1" />
          {/* Session-aware: one login for everyone. Logged-in visitors get ONE
              identity control — an avatar (or person icon) that opens the
              context switcher with log out at the bottom. "Your account" is
              already the consumer context inside that menu, so there is no
              separate button for it. */}
          {user ? (
            <ContextSwitcher variant="avatar" />
          ) : (

            <button
              type="button"
              onClick={() => navigate("/login?next=%2Faccount")}
              data-testid="landing-login"
              className="hidden min-h-[44px] items-center rounded-full border border-ink/20 bg-white px-[18px] text-[15px] font-semibold text-ink transition-colors hover:border-ink sm:flex"
            >
              {t("landing.nav.login")}
            </button>
          )}

          <button
            type="button"
            onClick={() => navigate(funnelHref)}
            className="flex min-h-[44px] items-center rounded-full bg-grape px-[22px] text-[15px] font-semibold text-grape-foreground transition-colors hover:bg-tangerine"
          >
            {t("landing.nav.cta")}
          </button>
          <button
            type="button"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            className="lg:hidden"
            onClick={() => setMenuOpen((o) => !o)}
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        {menuOpen && (
          <nav aria-label="Mobile" className="border-t border-ink/10 bg-paper px-6 py-4 lg:hidden">
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setMenuOpen(false)}
                className="block py-2 text-[15px] font-medium text-ink"
              >
                {l.label}
              </a>
            ))}
          </nav>
        )}
      </header>

      <main>
        {/* Hero */}
        <section className={`${shell} pt-12 md:pt-16`}>
          <div className="grid items-center gap-10 lg:grid-cols-[minmax(320px,1.05fr)_minmax(400px,0.95fr)]">
            <div>
              <div className="mb-[22px] inline-flex items-center gap-2 rounded-full bg-volt px-[15px] py-2">
                <Star className="h-[15px] w-[15px] text-volt-foreground" />
                <span className="text-[12.5px] font-semibold tracking-[0.06em] text-volt-foreground">
                  {t("landing.hero.badge")}
                </span>
              </div>

              <h1 className="mb-5 font-brand text-[clamp(3rem,8.4vw,82px)] font-semibold leading-[0.96] tracking-[-0.035em]">
                {t("landing.hero.headline.line1")}
                <br />
                {t("landing.hero.headline.line2")}
              </h1>

              <p className="mb-[30px] max-w-[44ch] text-[clamp(1.0625rem,2vw,21px)] leading-[1.45] text-ink/70">
                {t("landing.hero.subcopy")}
              </p>

              <div className="mb-[18px] flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => navigate(funnelHref)}
                  className="flex min-h-[58px] items-center justify-center rounded-full bg-grape px-8 text-[17px] font-semibold text-grape-foreground transition-colors hover:bg-tangerine"
                >
                  {t("landing.cta.primary")}
                </button>
                <a
                  href="#how"
                  className="flex min-h-[58px] items-center justify-center rounded-full border border-ink/20 px-7 text-base font-semibold text-ink transition-colors hover:border-ink"
                >
                  {t("landing.cta.secondary")}
                </a>
              </div>
              {resolveFigures(t("landing.hero.reassurance"), figures) && (
                <p className="text-[15px] leading-[1.5] text-ink/70">
                  {resolveFigures(t("landing.hero.reassurance"), figures)}
                </p>
              )}
            </div>

            {/* Hero reel + style tiles */}
            <div className="flex items-center gap-4 sm:gap-6">
              <div className="relative aspect-[9/16] min-w-[140px] flex-[0_1_300px] sm:min-w-[210px] overflow-hidden rounded-[26px] bg-paper shadow-[0_26px_60px_rgba(28,32,48,0.28)]">
                <HeroVideo
                  src={heroVideoUrl}
                  poster={heroPosterUrl}
                  label="Cardmoji avatar card styles reel"
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </div>


              <div className="flex min-w-[92px] flex-1 flex-col gap-2.5 sm:min-w-[132px]">
                <span className="whitespace-nowrap text-[12px] font-semibold uppercase tracking-[0.1em] text-slate-muted">
                  {t("landing.hero.styles.count")}
                </span>
                <Link
                  to={`/create/style${location.search}`}
                  className="relative block aspect-[260/412] w-full overflow-hidden rounded-xl bg-tangerine transition-transform hover:-translate-y-[3px]"
                >
                  <img
                    src={styleCatUrl}
                    alt={t("landing.media.style.cat.alt")}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                </Link>
                <Link
                  to={`/create/style${location.search}`}
                  className="relative block aspect-[260/412] w-full overflow-hidden rounded-xl bg-amber transition-transform hover:-translate-y-[3px]"
                >
                  <img
                    src={styleDogUrl}
                    alt={t("landing.media.style.dog.alt")}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                </Link>
                <Link
                  to={`/create/style${location.search}`}
                  className="whitespace-nowrap text-[14px] font-semibold text-grape"
                >
                  {t("landing.hero.styles.link")}
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section id="how" className={`${shell} scroll-mt-20 pt-16 md:pt-[92px]`}>
          <span className={eyebrow}>{t("landing.howitworks.eyebrow")}</span>
          <h2 className={`${h2Class} mb-8 mt-3`}>{t("landing.howitworks.heading")}</h2>

          <div className="grid gap-[18px] md:grid-cols-3">
            <div className="flex flex-col gap-3.5 rounded-[22px] bg-volt p-[30px]">
              <span className="font-brand text-[52px] font-semibold leading-none tracking-[-0.03em] text-ink">
                1
              </span>
              <h3 className="font-brand text-[26px] font-semibold leading-[1.15] tracking-[-0.02em]">
                {t("landing.howitworks.step1.title")}
              </h3>
              <p className="text-base leading-[1.5] text-ink/80">{t("landing.howitworks.step1")}</p>
            </div>
            <div className="flex flex-col gap-3.5 rounded-[22px] bg-grape p-[30px] text-grape-foreground">
              <span className="font-brand text-[52px] font-semibold leading-none tracking-[-0.03em] text-volt">
                2
              </span>
              <h3 className="font-brand text-[26px] font-semibold leading-[1.15] tracking-[-0.02em]">
                {t("landing.howitworks.step2.title")}
              </h3>
              <p className="text-base leading-[1.5]">{t("landing.howitworks.step2")}</p>
            </div>
            <div className="flex flex-col gap-3.5 rounded-[22px] bg-leaf p-[30px] text-leaf-foreground">
              <span className="font-brand text-[52px] font-semibold leading-none tracking-[-0.03em]">
                3
              </span>
              <h3 className="font-brand text-[26px] font-semibold leading-[1.15] tracking-[-0.02em]">
                {t("landing.howitworks.step3.title")}
              </h3>
              <p className="text-base leading-[1.5]">{t("landing.howitworks.step3")}</p>
            </div>
          </div>

          <div className="mt-6 flex flex-col items-start gap-5 rounded-[20px] border border-ink/10 bg-white px-7 py-[22px] sm:flex-row sm:items-center">
            <span className="flex-1 text-base leading-[1.5] text-ink/[0.78]">
              {t("landing.howitworks.footnote")}
            </span>
            <button
              type="button"
              onClick={() => navigate(funnelHref)}
              className="flex min-h-[50px] shrink-0 items-center rounded-full bg-ink px-[26px] text-base font-semibold text-ink-foreground transition-colors hover:bg-grape"
            >
              {t("landing.howitworks.cta")}
            </button>
          </div>
        </section>

        {/* Creators */}
        <section id="creators" className={`${shell} scroll-mt-20 pt-16 md:pt-[92px]`}>
          <div className="rounded-[30px] bg-ink p-8 text-ink-foreground sm:p-14">
            <div className="grid items-center gap-12 lg:grid-cols-2">
              <div>
                <span className="text-[12px] font-semibold uppercase tracking-[0.14em] text-volt">
                  {t("landing.creators.eyebrow")}
                </span>
                <h2 className="mb-4 mt-3 font-brand text-[clamp(2rem,4.4vw,46px)] font-semibold leading-[1.04] tracking-[-0.03em]">
                  {t("landing.creators.heading")}
                </h2>
                <p className="mb-[26px] max-w-[46ch] text-[18px] leading-[1.5]">
                  {t("landing.creators.body")}
                </p>
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => navigate("/creator")}
                    className="flex min-h-[56px] items-center rounded-full bg-volt px-[30px] text-[17px] font-semibold text-volt-foreground transition-colors hover:bg-paper"
                  >
                    {t("landing.creators.cta.primary")}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/creator")}
                    className="flex min-h-[56px] items-center rounded-full border border-paper/35 px-[26px] text-base font-semibold text-paper transition-colors hover:border-paper"
                  >
                    {t("landing.creators.cta.secondary")}
                  </button>

                </div>
              </div>
              <div className="flex flex-col gap-3">
                {creatorStats.map((s) => (
                  <div
                    key={s.n}
                    className="flex items-start gap-4 rounded-[18px] bg-paper/[0.08] px-[22px] py-5"
                  >
                    <span className="shrink-0 font-brand text-[30px] font-semibold leading-[1.1] text-volt">
                      {s.n}
                    </span>
                    <span className="text-base leading-[1.45]">
                      <strong className="font-semibold">{s.strong}</strong>
                      {s.rest}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Issuing banks */}
        <section id="banks" className={`${shell} scroll-mt-20 pt-16 md:pt-[92px]`}>
          <span className={eyebrow}>{t("landing.banks.eyebrow")}</span>
          <h2 className={`${h2Class} mb-4 mt-3 max-w-[26ch]`}>{t("landing.banks.heading")}</h2>
          <p className="mb-5 max-w-[62ch] text-[18px] leading-[1.5] text-ink/70">
            {t("landing.banks.body")}
          </p>
          {/* Deck shown inline (it advances itself every 10s). The "open" link
              points at the public site, never a Lovable preview URL, which
              would bounce a visitor to a Lovable login. */}
          <div className="mb-4 overflow-hidden rounded-[20px] border border-ink/10 bg-ink">
            <iframe
              src="/deck/cardmoji-deck.html"
              title="Cardmoji deck for card issuers"
              loading="lazy"
              className="block aspect-[16/10] w-full border-0"
            />
          </div>
          <a
            href="https://www.cardmoji.com/deck/cardmoji-deck.html"
            target="_blank"
            rel="noopener"
            className="mb-9 inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white px-5 py-2.5 text-[15px] font-semibold text-ink hover:border-ink/40"
          >
            {t("landing.banks.deck")} →
          </a>

          <div className="grid items-start gap-7 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="grid gap-3.5 sm:grid-cols-2">
              {bankCards.map((c) => (
                <div key={c.t} className="rounded-[20px] border border-ink/10 bg-white p-6">
                  <h3 className="mb-2 font-brand text-[20px] font-semibold">{c.t}</h3>
                  <p className="text-[15px] leading-[1.5] text-ink/75">{c.d}</p>
                </div>
              ))}
            </div>

            <div className="rounded-[24px] bg-azure p-8 text-azure-foreground">
              <h3 className="mb-2.5 font-brand text-[28px] font-semibold leading-[1.15] tracking-[-0.02em]">
                {t("landing.banks.form.heading")}
              </h3>
              <p className="mb-[22px] text-base leading-[1.5]">{t("landing.banks.form.body")}</p>
              <div className="flex flex-col gap-3.5">
                <div className="flex flex-col gap-[7px]">
                  <label htmlFor="bank-name" className="text-[13.5px] font-semibold">
                    {t("landing.banks.form.name.label")}
                  </label>
                  <input
                    id="bank-name"
                    value={bank.name}
                    onChange={(e) => setBank({ ...bank, name: e.target.value })}
                    placeholder={t("landing.banks.form.name.placeholder")}
                    className={fieldClass}
                  />
                </div>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <div className="flex min-w-0 flex-1 flex-col gap-[7px]">
                    <label htmlFor="bank-org" className="text-[13.5px] font-semibold">
                      {t("landing.banks.form.org.label")}
                    </label>
                    <input
                      id="bank-org"
                      value={bank.org}
                      onChange={(e) => setBank({ ...bank, org: e.target.value })}
                      placeholder={t("landing.banks.form.org.placeholder")}
                      className={fieldClass}
                    />
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col gap-[7px]">
                    <label htmlFor="bank-email" className="text-[13.5px] font-semibold">
                      {t("landing.banks.form.email.label")}
                    </label>
                    <input
                      id="bank-email"
                      type="email"
                      value={bank.email}
                      onChange={(e) => setBank({ ...bank, email: e.target.value })}
                      placeholder={t("landing.banks.form.email.placeholder")}
                      className={fieldClass}
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-[7px]">
                  <label htmlFor="bank-note" className="text-[13.5px] font-semibold">
                    {t("landing.banks.form.note.label")}
                  </label>
                  <textarea
                    id="bank-note"
                    rows={3}
                    value={bank.note}
                    onChange={(e) => setBank({ ...bank, note: e.target.value })}
                    placeholder={t("landing.banks.form.note.placeholder")}
                    className={`${fieldClass} resize-y leading-[1.5]`}
                  />
                </div>
                <button
                  type="button"
                  onClick={sendBank}
                  disabled={!ready || busy || sent}
                  className={`min-h-[56px] w-full rounded-full bg-volt px-4 text-[17px] font-semibold text-volt-foreground transition-all hover:bg-paper ${
                    ready && !sent ? "opacity-100" : "pointer-events-none opacity-40"
                  }`}
                >
                  {sent
                    ? t("landing.banks.form.sent")
                    : busy
                      ? t("landing.banks.form.sending")
                      : t("landing.banks.form.submit")}
                </button>
                <span className="text-[13.5px] leading-[1.45]">
                  {sent ? t("landing.banks.form.sentHint") : t("landing.banks.form.hint")}
                </span>
                {/* An enquiry is a tracked ticket now, so the enquirer gets a way
                    back to it. Only the id travels in the URL — the bearer token
                    stays in device storage, never in a link we render. */}
                {sent && ticketId && (
                  <Link
                    to={`/support/thread?id=${ticketId}`}
                    className="text-[13.5px] font-semibold underline underline-offset-4"
                  >
                    Track this enquiry
                  </Link>
                )}

              </div>
            </div>
          </div>
        </section>

        {/* About */}
        <section id="about" className={`${shell} scroll-mt-20 pt-16 md:pt-[92px]`}>
          <span className={eyebrow}>{t("landing.about.eyebrow")}</span>
          <h2 className={`${h2Class} mb-4 mt-3 max-w-[26ch]`}>{t("landing.about.heading")}</h2>
          <p className="mb-7 max-w-[62ch] text-[18px] leading-[1.55] text-ink/70">
            {t("landing.about.body")}
          </p>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-[22px] bg-volt p-[30px]">
              <div className="mb-3.5 flex items-center gap-2.5">
                <Star className="h-[22px] w-[22px] text-ink" />
                <span className="text-[12px] font-semibold uppercase tracking-[0.1em] text-ink">
                  {t("landing.about.card1.badge")}
                </span>
              </div>
              <h3 className="mb-2.5 font-brand text-[22px] font-semibold leading-[1.2]">
                {t("landing.about.card1.title")}
              </h3>
              <p className="text-[15px] leading-[1.5] text-ink">{t("landing.about.card1.body")}</p>
            </div>
            <div className="rounded-[22px] border border-ink/10 bg-white p-[30px]">
              <h3 className="mb-2.5 font-brand text-[22px] font-semibold leading-[1.2]">
                {t("landing.about.card2.title")}
              </h3>
              <p className="text-[15px] leading-[1.55] text-ink/75">
                {resolveFigures(t("landing.about.card2.body"), figures)}
              </p>

            </div>
            <div className="rounded-[22px] border border-ink/10 bg-white p-[30px]">
              <h3 className="mb-2.5 font-brand text-[22px] font-semibold leading-[1.2]">
                {t("landing.about.card3.title")}
              </h3>
              <p className="text-[15px] leading-[1.55] text-ink/75">{t("landing.about.card3.body")}</p>
            </div>
          </div>
        </section>

        {/* Closing CTA */}
        <section className={`${shell} pt-16 md:pt-[92px]`}>
          <div className="rounded-[30px] bg-grape px-8 py-14 text-center text-grape-foreground sm:p-[68px]">
            <h2 className="mx-auto mb-[18px] max-w-[20ch] font-brand text-[clamp(2.25rem,5.4vw,56px)] font-semibold leading-[1.02] tracking-[-0.03em]">
              {t("landing.closing.heading")}
            </h2>
            <p className="mx-auto mb-7 max-w-[44ch] text-[18px] leading-[1.5]">
              {t("landing.closing.body")}
            </p>
            <button
              type="button"
              onClick={() => navigate(funnelHref)}
              className="inline-flex min-h-[58px] items-center justify-center rounded-full bg-volt px-[38px] text-[17px] font-semibold text-volt-foreground transition-colors hover:bg-paper"
            >
              {t("landing.closing.cta")}
            </button>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className={`${shell} pb-16 pt-14`}>
        <div className="mb-[30px] h-px bg-ink/10" />
        <div className="flex flex-col gap-10 md:flex-row md:gap-12">
          <div className="flex-1">
            <span className="font-brand text-[20px] font-semibold tracking-[-0.02em]">
              {t("landing.nav.brand")}
            </span>
            <p className="mt-2.5 max-w-[52ch] text-[13.5px] leading-[1.55] text-ink/75">
              <strong className="font-semibold text-ink">{t("landing.disclaimer.lead")}</strong>{" "}
              {t("landing.disclaimer")}
            </p>

          </div>
          <div className="flex flex-wrap gap-12">
            <div className="flex flex-col gap-[11px]">
              <span className="text-[12px] font-semibold uppercase tracking-[0.1em] text-slate-muted">
                {t("landing.footer.product.title")}
              </span>
              <Link to={`/create/style${location.search}`} className="text-[14.5px] font-medium text-ink hover:text-grape">
                {t("landing.footer.product.gallery")}
              </Link>
              <a href="#how" className="text-[14.5px] font-medium text-ink hover:text-grape">
                {t("landing.footer.product.how")}
              </a>
              <Link to="/creator" className="text-[14.5px] font-medium text-ink hover:text-grape">
                {t("landing.footer.product.creators")}
              </Link>

            </div>
            <div className="flex flex-col gap-[11px]">
              <span className="text-[12px] font-semibold uppercase tracking-[0.1em] text-slate-muted">
                {t("landing.footer.partners.title")}
              </span>
              <a href="#banks" className="text-[14.5px] font-medium text-ink hover:text-grape">
                {t("landing.footer.partners.banks")}
              </a>
              <a href="#about" className="text-[14.5px] font-medium text-ink hover:text-grape">
                {t("landing.footer.partners.about")}
              </a>
              <Link to="/login?staff=1" className="text-[14.5px] font-medium text-ink/70 hover:text-grape">
                {t("landing.footer.partners.staff")}
              </Link>
            </div>
            <div className="flex flex-col gap-[11px]">
              <span className="text-[12px] font-semibold uppercase tracking-[0.1em] text-slate-muted">
                {t("landing.footer.legal.title")}
              </span>
              {/* L3: these render the CURRENT published version straight from the
                  Legal Register (/legal/<key>). Never point them at a second copy. */}
              <Link
                to="/legal/terms"
                className="text-[14.5px] font-medium text-grape underline underline-offset-2 hover:text-ink"
              >
                {t("landing.footer.legal.terms")}
              </Link>
              <Link
                to="/legal/privacy"
                className="text-[14.5px] font-medium text-grape underline underline-offset-2 hover:text-ink"
              >
                {t("landing.footer.legal.privacy")}
              </Link>
              <Link
                to="/legal/biometric"
                className="text-[14.5px] font-medium text-grape underline underline-offset-2 hover:text-ink"
              >
                {t("landing.footer.legal.biometric")}
              </Link>

            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
